import React, { useContext } from 'react';
import { NodeProps } from 'reactflow';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as Fire } from '../../../../../images/icons/fire-solid.svg';
import { getNullSelectOption, Select } from '../../../../../shared/form/inputs/Select';
import { HeatSourceFormModel } from '../../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { useTree } from '../map-helpers/hooks/useTree';
import { getCapacityString, generatedPressureString } from '../map-helpers/mapHelpers';
import { HeatSourceMapNodeData } from '../mapNode';
import { TreeNode, TreeNodeType } from '../treeNode';
import { MapFlowNode } from './MapFlowNode';

export const HeatSource = ({
  data: {
    heatSource,
    treeNodeId,
    generatedPressureInKilopascals,
    indexCircuitStats: { requiredPressureInKilopascals, availablePressureInKilopascals, pumpCapacityPercentage },
    state,
    markedForDeletion,
  },
}: NodeProps<HeatSourceMapNodeData>) => {
  const { tree, replaceNode } = useTree();
  const { referenceData } = assertNotNull(useContext(QuoteOrDesignFormContext));

  const currentHeatSource =
    referenceData.heatSourceOptions.find(heatSourceOption => heatSourceOption.value?.displayName === heatSource)
      ?.value ?? getNullSelectOption<HeatSourceFormModel>().value;

  const onChangeHeatSource = (newHeatSource: HeatSourceFormModel | null) => {
    const nodeToReplace = tree[treeNodeId];
    if (nodeToReplace.nodeType !== TreeNodeType.HeatSource) {
      return;
    }

    const newNode: TreeNode = {
      ...nodeToReplace,
      fieldData: {
        heatSource: newHeatSource?.displayName ?? null,
      },
    };
    replaceNode(treeNodeId, newNode);
  };

  const generatedPressure = generatedPressureString(generatedPressureInKilopascals);
  const capacity = getCapacityString({
    requiredPressureInKilopascals,
    availablePressureInKilopascals,
    pumpCapacityPercentage,
  });

  return (
    <MapFlowNode
      icon={<HeatSourceIcon />}
      hasSource={true}
      hasTarget={false}
      stats={[generatedPressure, capacity]}
      title="Heat source"
      state={state}
      markedForDeletion={markedForDeletion}
    >
      <Select
        name="heat-source"
        value={currentHeatSource}
        options={referenceData.heatSourceOptions}
        isSmall={true}
        onChange={onChangeHeatSource}
      />
    </MapFlowNode>
  );
};

const HeatSourceIcon = styled(Fire)`
  height: 1em;
  width: 1em;
`;
